<template>
  <vx-card>
    <h4>ตั้งเวลา Task</h4>
    <p class="mt-3">
      เวลาไทย Asia/Bangkok บันทึกแล้วมีผลหลังผู้ดูแล restart/reload Backend
      เท่านั้น ระบบไม่ reload ให้เอง
    </p>
    <p class="mt-2 text-warning">
      ตรวจว่างานสำคัญจบแล้วก่อน restart
      การตั้งถี่เกินไปอาจทำให้งานซ้อนหรือเพิ่มภาระฐานข้อมูล
    </p>
    <p class="mt-2">
      Cron 5 ช่อง: นาที ชั่วโมง วันที่ เดือน วันในสัปดาห์ หรือ 6
      ช่องโดยเพิ่มวินาทีด้านหน้า เช่น 30 8 * * * = ทุกวัน 08:30 น.
    </p>
    <p v-if="loading" role="status">กำลังโหลดตารางเวลา…</p>
    <p v-if="error" role="alert" class="text-danger">
      {{ error }} <vs-button type="border" @click="load">ลองใหม่</vs-button>
    </p>
    <form @submit.prevent="askSave">
      <section
        v-for="(row, index) in rows"
        :key="row.taskType"
        class="task-section"
        :aria-label="row.title"
      >
        <div class="task-heading">
          <span class="task-number" aria-hidden="true">{{
            String(index + 1).padStart(2, "0")
          }}</span>
          <div class="task-identity">
            <h5>{{ row.title }}</h5>
            <code class="task-key">{{ row.taskType }}</code>
            <span v-if="row.locked" class="task-badge">ล็อกจากระบบ</span>
            <span v-else-if="dirty(row)" class="task-badge task-badge-dirty"
              >ยังไม่ได้บันทึก</span
            >
          </div>
          <label class="task-enabled"
            ><vs-switch
              type="button"
              v-model="draft[row.taskType].enabled"
              :disabled="row.locked || !row.ready || busy"
              :aria-label="'เปิดใช้งาน ' + row.title"
            /><span>เปิดใช้งานตามเวลา</span></label
          >
        </div>
        <p v-if="row.message" class="task-notice">{{ row.message }}</p>
        <div class="task-fields">
          <div class="task-expression-group">
            <div class="task-expression">
              <span class="task-field-label">Cron expression</span>
              <div class="task-input-actions">
                <input
                  v-model="draft[row.taskType].expression"
                  :disabled="row.locked || !row.ready || busy"
                  maxlength="200"
                  :aria-invalid="Boolean(validation[row.taskType])"
                  :aria-label="'Cron expression ' + row.title"
                  @input="validateRow(row)"
                />
                <vs-button
                  type="border"
                  :disabled="row.locked || !row.ready || busy"
                  @click="openTemplates(row)"
                  >เลือก Template</vs-button
                >
              </div>
            </div>
            <p class="task-default">
              ค่าเริ่มต้น <code>{{ row.defaultExpression }}</code>
            </p>
          </div>
          <div
            class="task-description"
            :aria-label="'คำอธิบายเวลา ' + row.title"
          >
            <span class="task-field-label"
              >Cron description
              <span class="task-timezone">· เวลาไทย</span></span
            >
            <p>{{ describeCronExpression(draft[row.taskType].expression) }}</p>
          </div>
        </div>
        <p
          v-if="validation[row.taskType]"
          class="text-danger mt-2"
          role="alert"
        >
          {{ validation[row.taskType] }}
        </p>
      </section>
      <div class="task-actions">
        <vs-button
          type="border"
          :disabled="busy || loading || !changed.length"
          @click="resetAll"
          >คืนค่าทั้งหมด</vs-button
        >
        <vs-button
          :disabled="busy || loading || !changed.length"
          @click="askSave"
          >{{ busy ? "กำลังบันทึก…" : "บันทึกทั้งหมด" }}</vs-button
        >
      </div>
      <p v-if="message" class="mt-3" role="status">{{ message }}</p>
    </form>
    <vs-popup
      class="cron-template-popup"
      title="เลือก Template ตารางเวลา"
      :active.sync="templateOpen"
    >
      <p class="mb-4">
        {{ templateTitle }} · เวลาไทย Asia/Bangkok — กดแถวเพื่อเลือก
        จะเติมค่าแต่ยังไม่บันทึก
      </p>
      <p class="text-warning mb-4">
        ตรวจความเหมาะสมก่อนเลือกความถี่สูง โดยเฉพาะงานส่งข้อความหรือแก้ข้อมูล
      </p>
      <div class="template-table-wrap">
        <table class="template-table">
          <thead>
            <tr>
              <th scope="col">ประเภท</th>
              <th scope="col">Cron expression</th>
              <th scope="col">Cron description</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in templateOptions"
              :key="item.expression"
              tabindex="0"
              role="button"
              :aria-label="'เลือก ' + item.description"
              @click="selectTemplate(item)"
              @keydown.enter.prevent="selectTemplate(item)"
              @keydown.space.prevent="selectTemplate(item)"
            >
              <td>{{ item.group }}</td>
              <td>
                <code>{{ item.expression }}</code>
              </td>
              <td>{{ item.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </vs-popup>
  </vx-card>
</template>
<script>
import gql from "graphql-tag";
import { apolloClient } from "@/setup/apollo-setup";
import {
  getCronExpressionError,
  cronTemplates,
  describeCronExpression,
} from "@/utils/cronExpression";

const fields = gql`
  fragment TaskScheduleFields on TaskSchedule {
    taskType
    title
    defaultExpression
    locked
    enabled
    expression
    expectedValue
    ready
    message
    timezone
  }
`;
const read = gql`
  query TaskSchedules {
    taskSchedules {
      ...TaskScheduleFields
    }
  }
  ${fields}
`;
const update = gql`
  mutation UpdateTaskSchedules($values: [TaskScheduleInput!]!) {
    updateTaskSchedules(values: $values) {
      ...TaskScheduleFields
    }
  }
  ${fields}
`;

export default {
  data() {
    return {
      loading: true,
      rows: [],
      draft: {},
      validation: {},
      busy: false,
      message: "",
      error: "",
      disposed: false,
      templateOpen: false,
      templateTaskType: null,
      templateOptions: cronTemplates,
    };
  },
  computed: {
    templateTitle() {
      const row = this.rows.find(
        (item) => item.taskType === this.templateTaskType
      );
      return row ? row.title : "";
    },
    changed() {
      return this.rows.filter(
        (row) => !row.locked && row.ready && this.dirty(row)
      );
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.disposed = true;
  },
  methods: {
    describeCronExpression,
    openTemplates(row) {
      if (this.busy || this.loading || row.locked || !row.ready) return;
      this.templateTaskType = row.taskType;
      this.templateOpen = true;
    },
    selectTemplate(item) {
      const row = this.rows.find(
        (value) => value.taskType === this.templateTaskType
      );
      if (
        !this.templateOpen ||
        !row ||
        row.locked ||
        !row.ready ||
        this.busy ||
        this.disposed
      )
        return;
      if (
        !this.templateOptions.includes(item) ||
        getCronExpressionError(item.expression)
      )
        return;
      this.$set(this.draft[row.taskType], "expression", item.expression);
      this.validateRow(row);
      this.templateOpen = false;
      this.templateTaskType = null;
    },
    dirty(row) {
      const value = this.draft[row.taskType];
      return (
        value &&
        (value.enabled !== row.enabled || value.expression !== row.expression)
      );
    },
    reset(row) {
      this.$set(this.draft, row.taskType, {
        enabled: row.enabled,
        expression: row.expression,
      });
      this.$set(this.validation, row.taskType, "");
    },
    resetAll() {
      if (this.busy) return;
      this.rows.forEach((row) => this.reset(row));
      this.message = "";
    },
    validateRow(row) {
      if (row.locked || !row.ready) return true;
      const value = this.draft[row.taskType];
      const error =
        typeof value.enabled !== "boolean"
          ? "กรุณาระบุสถานะเปิดใช้งานให้ถูกต้อง"
          : getCronExpressionError(value.expression);
      const valid = !error;
      this.$set(this.validation, row.taskType, error);
      return valid;
    },
    validateAll() {
      const valid = this.rows
        .map((row) => this.validateRow(row))
        .every(Boolean);
      if (!valid) this.message = "ยังไม่ได้บันทึก กรุณาแก้ค่าที่แจ้งเตือน";
      return valid;
    },
    async load() {
      if (this.busy) return;
      this.loading = true;
      this.error = "";
      try {
        const { data } = await apolloClient.query({
          query: read,
          fetchPolicy: "no-cache",
        });
        if (this.disposed) return;
        this.rows = data.taskSchedules;
        this.resetAll();
      } catch (error) {
        if (!this.disposed)
          this.error =
            "โหลดตารางเวลาไม่ได้ กรุณาตรวจสิทธิ์ root การเชื่อมต่อ และ SystemSetting";
      } finally {
        if (!this.disposed) this.loading = false;
      }
    },
    askSave() {
      if (
        this.busy ||
        this.loading ||
        !this.changed.length ||
        !this.validateAll()
      )
        return;
      this.$vs.dialog({
        type: "confirm",
        title: "ยืนยันบันทึกตารางเวลาทั้งหมด",
        text: "บันทึกรายการที่แก้ไขพร้อมกันหรือไม่? มีผลหลังผู้ดูแล restart Backend เท่านั้น",
        acceptText: "บันทึก",
        cancelText: "ยกเลิก",
        accept: () => this.save(),
      });
    },
    async save() {
      if (
        this.disposed ||
        this.busy ||
        this.loading ||
        !this.changed.length ||
        !this.validateAll()
      )
        return;
      const values = this.changed.map((row) => ({
        taskType: row.taskType,
        ...this.draft[row.taskType],
        expectedValue: row.expectedValue,
      }));
      this.busy = true;
      this.message = "";
      try {
        const { data } = await apolloClient.mutate({
          mutation: update,
          variables: { values },
        });
        if (this.disposed) return;
        data.updateTaskSchedules.forEach((saved) => {
          this.$set(
            this.rows,
            this.rows.findIndex((row) => row.taskType === saved.taskType),
            saved
          );
          this.reset(saved);
        });
        this.message =
          "บันทึกทั้งหมดแล้ว รอผู้ดูแล restart/reload Backend เพื่อใช้เวลาใหม่";
      } catch (error) {
        if (this.disposed) return;
        const safe = (error.graphQLErrors || []).find(
          (item) =>
            item.extensions && item.extensions.code === "task_schedule_invalid"
        );
        this.message = safe
          ? safe.message
          : "ยังยืนยันผลบันทึกไม่ได้ กรุณาโหลดหน้าใหม่เพื่อตรวจสอบก่อนลองอีกครั้ง";
        if (safe && safe.extensions.taskType)
          this.$set(this.validation, safe.extensions.taskType, safe.message);
      } finally {
        if (!this.disposed) this.busy = false;
      }
    },
  },
};
</script>
<style scoped>
.task-description {
  min-width: 0;
  padding: 14px 18px;
  border-left: 3px solid #c8dce9;
  border-radius: 0 6px 6px 0;
  background: #f4f8fb;
}
.task-description p {
  margin: 8px 0 0;
  line-height: 1.65;
  font-weight: 500;
  color: #244962;
  overflow-wrap: anywhere;
}
.template-table-wrap {
  overflow-x: auto;
}
.template-table {
  width: 100%;
  border-collapse: collapse;
}
.template-table th,
.template-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ebe9f1;
}
.template-table code {
  white-space: nowrap;
}
.template-table tbody tr {
  cursor: pointer;
}
.template-table tbody tr:hover,
.template-table tbody tr:focus {
  background: #eeeaff;
  outline: 2px solid #7367f0;
  outline-offset: -2px;
}
.cron-template-popup >>> .vs-popup {
  width: 850px;
  max-width: calc(100vw - 32px);
}
.task-section {
  border-top: 1px solid #ebe9f1;
  margin-top: 24px;
  padding: 24px 0 4px;
}
.task-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}
.task-number {
  flex: 0 0 32px;
  height: 32px;
  border-radius: 6px;
  background: #edf3f7;
  color: #375d78;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}
.task-identity {
  min-width: 0;
  flex: 1;
}
.task-identity h5 {
  margin: 0 0 6px;
  line-height: 1.5;
  font-size: 15px;
  font-weight: 600;
}
.task-key {
  display: inline;
  font-size: 11px;
  color: #737e8a;
  background: transparent;
  padding: 0;
  overflow-wrap: anywhere;
}
.task-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #eef0f3;
  color: #616d7b;
  font-size: 11px;
}
.task-badge-dirty {
  color: #986008;
  background: #fff5dc;
}
.task-notice {
  margin: 0 0 16px 44px;
  color: #986008;
  line-height: 1.6;
}
.task-field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #586574;
}
.task-timezone {
  font-weight: 400;
  color: #778594;
}
.task-input-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.task-input-actions > button {
  white-space: nowrap;
  flex-shrink: 0;
}
.task-default {
  margin: 8px 0 0;
  color: #778594;
  font-size: 11px;
}
.task-default code {
  margin-left: 6px;
  background: transparent;
  color: inherit;
  padding: 0;
}
.task-expression-group {
  min-width: 0;
}
.task-enabled >>> .vs-switch {
  flex-shrink: 0;
}
.task-expression input:focus {
  outline: 2px solid #799eb9;
  outline-offset: 1px;
}
.task-expression input:disabled {
  background: #f5f6f8;
  color: #818b96;
}
@media (max-width: 1000px) {
  .task-fields {
    grid-template-columns: minmax(0, 1fr) !important;
  }
  .task-heading {
    flex-wrap: wrap;
  }
  .task-enabled {
    margin-left: 44px;
  }
}
@media (max-width: 480px) {
  .task-input-actions {
    flex-wrap: wrap;
  }
  .task-input-actions > button {
    width: 100%;
  }
}
.task-fields {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}
.task-enabled {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
  font-size: 12px;
  color: #637180;
  padding-top: 4px;
}
.task-expression {
  min-width: 0;
}
.task-expression input {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  height: 40px;
  font-family: monospace;
}
.task-expression input[aria-invalid="true"] {
  border-color: #ea5455;
}
.task-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}
</style>
